/** Facts about a sealed product's contents (extracted at import; see scripts/lib/sealed-contents.mjs). */
export interface SealedContents {
  packs?: number;
  cardsPerPack?: number;
  promos?: string[];
  promoCount?: number;
  deckCards?: number;
  sleeves?: number;
  energies?: number;
  items?: string[];
}

const ITEM_LABEL: Record<string, { es: string; en: string }> = {
  codeCard: { es: "Código para jugar online (Pokémon TCG Live)", en: "Code card for Pokémon TCG Live" },
  playerGuide: { es: "Guía del jugador", en: "Player's guide" },
  storageBox: { es: "Caja para guardar todo, con separadores", en: "Storage box with dividers" },
  deckBox: { es: "Deck box", en: "Deck box" },
  dice: { es: "Dados de daño", en: "Damage dice" },
  coin: { es: "Moneda", en: "Coin" },
  damageCounters: { es: "Contadores de daño", en: "Damage counters" },
  conditionMarkers: { es: "Marcadores de estado", en: "Condition markers" },
  stickers: { es: "Stickers", en: "Stickers" },
  rulebook: { es: "Reglamento", en: "Rulebook" },
  playmat: { es: "Playmat", en: "Playmat" },
  binder: { es: "Carpeta", en: "Binder" },
  pin: { es: "Pin", en: "Pin" },
  figure: { es: "Figura", en: "Figure" },
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** What's in the box, as short lines in the shopper's language. Empty when nothing is known. */
export function sealedContentsLines(c: SealedContents | null | undefined, locale: "es" | "en"): string[] {
  if (!c) return [];
  const es = locale === "es";
  const lines: string[] = [];
  if (c.packs) {
    const packs = es ? plural(c.packs, "sobre", "sobres") : plural(c.packs, "booster pack", "booster packs");
    lines.push(c.cardsPerPack ? `${packs} ${es ? `de ${c.cardsPerPack} cartas` : `of ${c.cardsPerPack} cards`}` : packs);
  } else if (c.cardsPerPack) {
    lines.push(es ? `${c.cardsPerPack} cartas por sobre` : `${c.cardsPerPack} cards per pack`);
  }
  if (c.promos?.length || c.promoCount) {
    const n = Math.max(c.promoCount ?? 0, c.promos?.length ?? 0);
    const what = es ? plural(n, "carta promo", "cartas promo") : plural(n, "promo card", "promo cards");
    lines.push(c.promos?.length ? `${what}: ${c.promos.join(", ")}` : what);
  }
  if (c.deckCards) lines.push(es ? `Mazo listo para jugar de ${c.deckCards} cartas` : `Ready-to-play ${c.deckCards}-card deck`);
  if (c.sleeves) lines.push(es ? `${c.sleeves} fundas` : `${c.sleeves} card sleeves`);
  if (c.energies) lines.push(es ? `${c.energies} cartas de energía` : `${c.energies} Energy cards`);
  for (const item of c.items ?? []) {
    const label = ITEM_LABEL[item];
    if (label) lines.push(label[locale]);
  }
  return lines;
}
